class MomentsGiftsTheme {
  constructor() {
    this.runtime = this.readRuntime();
    this.languageChanging = false;
    this.refreshDomReferences();
    this.cartIsStale = false;
    this.pendingCartItemAnimation = false;
    this.cartRefreshId = 0;
    this.cartBackdropTimer = null;
    this.cartDrawerTimer = null;
    this.searchTimer = null;
    this.searchCollapseTimer = null;
    this.searchExpansionTimer = null;
    this.accountPopoverTimer = null;
    this.bind();
    this.setupCategoryRails();
    this.setupFeaturedCarousels();
  }

  readRuntime() {
    if (window.momentsGifts?.routes && window.momentsGifts?.strings) return window.momentsGifts;
    try {
      const runtime = JSON.parse(document.getElementById('momentsGiftsRuntime')?.textContent || '{}');
      window.momentsGifts = runtime;
      return runtime;
    } catch (error) {
      return { routes: {}, strings: {} };
    }
  }

  refreshDomReferences() {
    this.searchShell = document.querySelector('[data-header-search]');
    this.searchResultsPanel = document.querySelector('[data-search-results-panel]');
    this.cartDrawer = document.querySelector('[data-cart-drawer]');
    this.cartBackdrop = document.querySelector('[data-cart-backdrop]');
    this.accountMenu = document.querySelector('[data-account-menu]');
    this.accountPopover = document.querySelector('[data-account-popover]');
    this.themeColorMeta = document.querySelector('meta[name="theme-color"]');
  }

  syncBrowserChromeColor() {
    const color = getComputedStyle(document.documentElement).getPropertyValue('--background').trim();
    if (!color) return;
    document.documentElement.style.backgroundColor = color;
    document.body.style.backgroundColor = color;
    document.documentElement.style.colorScheme = document.documentElement.classList.contains('theme-dark') ? 'dark' : 'light';
    if (this.themeColorMeta) {
      const replacement = this.themeColorMeta.cloneNode();
      replacement.setAttribute('content', color);
      this.themeColorMeta.replaceWith(replacement);
      this.themeColorMeta = replacement;
    }
  }

  bind() {
    document.addEventListener('click', (event) => this.onClick(event));
    document.addEventListener('submit', (event) => this.onSubmit(event));
    document.addEventListener('input', (event) => this.onInput(event));
    document.addEventListener('change', (event) => this.onChange(event));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closeOverlays();
      if (event.key === 'Enter' && event.target instanceof HTMLInputElement && event.target.matches('[data-catalog-search]')) event.preventDefault();
    });
    window.addEventListener('popstate', () => {
      if (document.querySelector('.collection-layout')) this.navigateCatalog(window.location.href, false);
    });
    window.addEventListener('scroll', () => this.collapseCatalogSearch(), { passive: true });
    window.addEventListener('resize', () => this.syncCatalogSortWidth());
    this.restoreCategorySelection(window.location.href);
    this.focusCatalogSearchFromUrl(window.location.href);
    this.syncCatalogSortWidth();
  }

  onClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const productDetailsSummary = target.closest('[data-product-details] > summary');
    if (productDetailsSummary) {
      event.preventDefault();
      this.toggleProductDetails(productDetailsSummary.parentElement);
      return;
    }
    const expandedCatalogSearch = document.querySelector('[data-catalog-search-shell].is-expanded');
    if (expandedCatalogSearch && !target.closest('[data-catalog-search-shell]')) this.collapseCatalogSearch();
    const openCatalogFilter = document.querySelector('[data-catalog-filter-panel].is-open');
    if (openCatalogFilter && !target.closest('[data-catalog-filter-panel]') && !target.closest('[data-catalog-filter-toggle]')) this.closeCatalogFilter();
    if (target.closest('[data-search-open]')) this.openSearch();
    if (target.closest('[data-search-close]')) this.closeSearch();
    if (target.closest('[data-cart-open]')) this.openCart();
    const catalogSearchToggle = target.closest('[data-catalog-search-toggle]');
    if (catalogSearchToggle) {
      event.preventDefault();
      const shell = catalogSearchToggle.closest('[data-catalog-search-shell]');
      shell?.classList.add('is-expanded');
      shell?.closest('.collection-toolbar__actions')?.classList.add('is-search-expanded');
      catalogSearchToggle.setAttribute('aria-expanded', 'true');
      const input = shell?.querySelector('[data-catalog-search]');
      input?.focus({ preventScroll: true });
      if (input instanceof HTMLInputElement) input.setSelectionRange(input.value.length, input.value.length);
      return;
    }
    const catalogSearchClose = target.closest('[data-catalog-search-close]');
    if (catalogSearchClose) {
      event.preventDefault();
      this.collapseCatalogSearch(true);
      return;
    }
    const catalogFilterToggle = target.closest('[data-catalog-filter-toggle]');
    if (catalogFilterToggle) {
      event.preventDefault();
      const panelId = catalogFilterToggle.getAttribute('aria-controls');
      const panel = panelId ? document.getElementById(panelId) : null;
      if (panel) {
        const expanded = catalogFilterToggle.getAttribute('aria-expanded') === 'true';
        if (expanded) {
          this.closeCatalogFilter();
        } else {
          catalogFilterToggle.setAttribute('aria-expanded', 'true');
          panel.classList.add('is-open');
        }
      }
      return;
    }
    const accountTrigger = target.closest('[data-account-trigger]');
    if (accountTrigger) {
      event.preventDefault();
      this.toggleAccountMenu();
      return;
    }
    if (target.closest('[data-cart-close]') || target === this.cartBackdrop) this.closeCart();
    const themeToggle = target.closest('[data-theme-toggle]');
    if (themeToggle) this.toggleTheme(themeToggle);
    const categoryRailButton = target.closest('[data-category-rail-scroll]');
    if (categoryRailButton) {
      event.preventDefault();
      this.scrollCategoryRail(categoryRailButton);
      return;
    }
    const featuredCarouselButton = target.closest('[data-featured-carousel-scroll]');
    if (featuredCarouselButton) {
      event.preventDefault();
      this.shiftFeaturedCarousel(featuredCarouselButton);
      return;
    }
    const quantityButton = target.closest('[data-quantity-change]');
    if (quantityButton) this.changeQuantity(quantityButton);
    const cartLineButton = target.closest('[data-cart-line-change]');
    if (cartLineButton) {
      event.preventDefault();
      event.stopPropagation();
      this.changeCartLine(cartLineButton);
      return;
    }
    const applyCartButton = target.closest('[data-cart-apply]');
    if (applyCartButton) {
      event.preventDefault();
      event.stopPropagation();
      this.applyCartChanges(applyCartButton);
      return;
    }
    const removeButton = target.closest('[data-confirm-remove]');
    if (removeButton) {
      event.preventDefault();
      event.stopPropagation();
      this.openConfirmation(removeButton);
      return;
    }
    if (target.closest('[data-dialog-cancel]')) {
      event.preventDefault();
      this.closeConfirmation();
      return;
    }
    const confirmRemove = target.closest('[data-dialog-confirm]');
    if (confirmRemove) {
      event.preventDefault();
      event.stopPropagation();
      this.removeCartItem(confirmRemove);
      return;
    }
    if (this.searchShell?.classList.contains('is-open') && !target.closest('[data-header-search]')) this.closeSearch();
    if (this.accountPopover?.classList.contains('is-open') && !target.closest('[data-account-menu]')) this.closeAccountMenu();
  }

  scrollCategoryRail(button) {
    const rail = button.closest('.category-product-row')?.querySelector('[data-category-product-rail]');
    if (!rail) return;
    const direction = Number(button.dataset.categoryRailScroll) || 1;
    const isRtl = getComputedStyle(rail).direction === 'rtl';
    const maxScroll = Math.max(rail.scrollWidth - rail.clientWidth, 0);
    const currentPosition = Math.min(Math.abs(rail.scrollLeft), maxScroll);
    const step = Math.max(rail.clientWidth * .82, 240);
    const targetPosition = Math.min(Math.max(currentPosition + direction * step, 0), maxScroll);
    rail.scrollTo({
      left: isRtl ? -targetPosition : targetPosition,
      behavior: 'smooth'
    });
  }

  setupCategoryRails() {
    this.categoryRailController?.abort();
    this.categoryRailObservers?.forEach((observer) => observer.disconnect());
    this.categoryRailController = new AbortController();
    this.categoryRailObservers = [];

    document.querySelectorAll('[data-category-product-rail]').forEach((rail) => {
      const update = () => this.updateCategoryRailControls(rail);
      rail.addEventListener('scroll', update, {
        passive: true,
        signal: this.categoryRailController.signal
      });
      const observer = new ResizeObserver(update);
      observer.observe(rail);
      this.categoryRailObservers.push(observer);
      requestAnimationFrame(update);
    });
  }

  updateCategoryRailControls(rail) {
    const row = rail.closest('.category-product-row');
    if (!row) return;
    const maxScroll = Math.max(rail.scrollWidth - rail.clientWidth, 0);
    const isRtl = getComputedStyle(rail).direction === 'rtl';
    const position = isRtl ? Math.abs(rail.scrollLeft) : rail.scrollLeft;
    const canScrollBackward = maxScroll > 2 && position > 2;
    const canScrollForward = maxScroll > 2 && position < maxScroll - 2;

    row.querySelectorAll('[data-category-rail-scroll]').forEach((button) => {
      const isBackward = Number(button.dataset.categoryRailScroll) < 0;
      const available = isBackward ? canScrollBackward : canScrollForward;
      button.dataset.categoryRailAvailable = String(available);
      button.disabled = !available;
      button.setAttribute('aria-hidden', String(!available));
    });
  }

  setupFeaturedCarousels() {
    this.featuredCarouselController?.abort();
    this.featuredCarouselStates?.forEach((state) => {
      clearInterval(state.timer);
      clearTimeout(state.wheelGestureTimer);
    });
    this.featuredCarouselController = new AbortController();
    this.featuredCarouselStates = [];

    document.querySelectorAll('[data-featured-carousel]').forEach((carousel) => {
      const slides = [...carousel.querySelectorAll('[data-featured-slide]')];
      if (slides.length < 2) return;
      const activeIndex = Math.max(slides.findIndex((slide) => slide.classList.contains('is-active')), 0);
      const state = {
        carousel,
        slides,
        index: activeIndex,
        timer: null,
        touchStart: null,
        wheelGesture: null,
        wheelGestureTimer: null,
        initialized: false
      };
      this.featuredCarouselStates.push(state);
      this.showFeaturedSlide(state, activeIndex);
      state.initialized = true;

      carousel.addEventListener('mouseenter', () => this.stopFeaturedCarousel(state), {
        signal: this.featuredCarouselController.signal
      });
      carousel.addEventListener('mouseleave', () => this.startFeaturedCarousel(state), {
        signal: this.featuredCarouselController.signal
      });
      carousel.addEventListener('focusin', () => this.stopFeaturedCarousel(state), {
        signal: this.featuredCarouselController.signal
      });
      carousel.addEventListener('focusout', () => {
        requestAnimationFrame(() => {
          if (!carousel.contains(document.activeElement)) this.startFeaturedCarousel(state);
        });
      }, { signal: this.featuredCarouselController.signal });
      carousel.addEventListener('touchstart', (event) => this.startFeaturedSwipe(state, event), {
        passive: true,
        signal: this.featuredCarouselController.signal
      });
      carousel.addEventListener('touchend', (event) => this.finishFeaturedSwipe(state, event), {
        passive: true,
        signal: this.featuredCarouselController.signal
      });
      carousel.addEventListener('touchcancel', () => {
        state.touchStart = null;
      }, { signal: this.featuredCarouselController.signal });
      carousel.addEventListener('wheel', (event) => this.scrollFeaturedCarousel(state, event), {
        passive: false,
        signal: this.featuredCarouselController.signal
      });
      this.startFeaturedCarousel(state);
    });

    document.addEventListener('visibilitychange', () => {
      this.featuredCarouselStates.forEach((state) => {
        if (document.hidden) this.stopFeaturedCarousel(state);
        else this.startFeaturedCarousel(state);
      });
    }, { signal: this.featuredCarouselController.signal });
  }

  startFeaturedCarousel(state) {
    this.stopFeaturedCarousel(state);
    if (document.hidden || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const interval = Number(state.carousel.dataset.featuredInterval) || 5000;
    state.timer = window.setInterval(() => {
      this.showFeaturedSlide(state, state.index + 1, 1);
    }, interval);
  }

  stopFeaturedCarousel(state) {
    if (!state?.timer) return;
    clearInterval(state.timer);
    state.timer = null;
  }

  shiftFeaturedCarousel(button) {
    const carousel = button.closest('[data-featured-carousel]');
    const state = this.featuredCarouselStates?.find((item) => item.carousel === carousel);
    if (!state) return;
    const direction = Number(button.dataset.featuredCarouselScroll) || 1;
    this.showFeaturedSlide(state, state.index + direction, direction);
    this.startFeaturedCarousel(state);
  }

  startFeaturedSwipe(state, event) {
    if (event.touches.length !== 1 || event.target.closest('a, button')) {
      state.touchStart = null;
      return;
    }
    const touch = event.touches[0];
    state.touchStart = { x: touch.clientX, y: touch.clientY };
    this.stopFeaturedCarousel(state);
  }

  finishFeaturedSwipe(state, event) {
    const start = state.touchStart;
    state.touchStart = null;
    const touch = event.changedTouches[0];
    if (!start || !touch) {
      this.startFeaturedCarousel(state);
      return;
    }

    const deltaX = touch.clientX - start.x;
    const deltaY = touch.clientY - start.y;
    if (Math.abs(deltaX) >= 44 && Math.abs(deltaX) > Math.abs(deltaY) * 1.15) {
      const rtlMultiplier = document.documentElement.dir === 'rtl' ? -1 : 1;
      const direction = (deltaX < 0 ? 1 : -1) * rtlMultiplier;
      this.showFeaturedSlide(state, state.index + direction, direction);
    }
    this.startFeaturedCarousel(state);
  }

  scrollFeaturedCarousel(state, event) {
    if (Math.abs(event.deltaX) < 12 || Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
    event.preventDefault();
    const magnitude = Math.abs(event.deltaX);
    const now = performance.now();
    const rtlMultiplier = document.documentElement.dir === 'rtl' ? -1 : 1;
    const direction = Math.sign(event.deltaX) * rtlMultiplier;
    clearTimeout(state.wheelGestureTimer);
    state.wheelGestureTimer = window.setTimeout(() => {
      state.wheelGesture = null;
    }, 180);

    if (!state.wheelGesture) {
      state.wheelGesture = {
        direction,
        lastMagnitude: magnitude,
        lastShiftAt: now,
        decaying: false
      };
      this.showFeaturedSlide(state, state.index + direction, direction);
      this.startFeaturedCarousel(state);
      return;
    }

    const gesture = state.wheelGesture;
    const directionChanged = direction !== gesture.direction
      && magnitude >= Math.max(18, gesture.lastMagnitude * .85);
    const newAcceleration = gesture.decaying
      && magnitude >= gesture.lastMagnitude * 1.75
      && magnitude - gesture.lastMagnitude >= 6;
    const isNewIntent = (directionChanged || newAcceleration) && now - gesture.lastShiftAt >= 90;

    if (isNewIntent) {
      gesture.direction = direction;
      gesture.lastShiftAt = now;
      gesture.decaying = false;
      this.showFeaturedSlide(state, state.index + direction, direction);
      this.startFeaturedCarousel(state);
    } else if (magnitude < gesture.lastMagnitude * .82) {
      gesture.decaying = true;
    }
    gesture.lastMagnitude = magnitude;
  }

  showFeaturedSlide(state, requestedIndex, direction = 1) {
    const nextIndex = (requestedIndex + state.slides.length) % state.slides.length;
    state.carousel.dataset.featuredDirection = direction < 0 ? 'previous' : 'next';
    state.slides.forEach((slide, index) => {
      const active = index === nextIndex;
      slide.classList.toggle('is-active', active);
      slide.classList.toggle('is-entering', active && state.initialized);
      slide.setAttribute('aria-hidden', String(!active));
      slide.inert = !active;
    });
    state.index = nextIndex;
  }

  onSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.id === 'HeaderLanguageForm') {
      event.preventDefault();
      this.changeLanguage(form);
      return;
    }
    if (form.matches('[data-ajax-cart-form]')) {
      event.preventDefault();
      this.addToCart(form);
      return;
    }
    if (form.matches('[data-catalog-filter-form], [data-catalog-sort-form]')) {
      event.preventDefault();
      this.applyCatalogFilters(form);
    }
  }

  onInput(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    if (input.matches('[data-catalog-search]')) {
      this.filterCatalog(input.value);
      return;
    }
    if (!input.matches('[data-predictive-input]')) return;
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.predict(input.value), 180);
  }

  onChange(event) {
    const input = event.target;
    if (input instanceof HTMLInputElement && input.matches('[data-catalog-category]')) {
      const allCategories = document.querySelector('[data-catalog-category][data-catalog-all]');
      const categoryInputs = [...document.querySelectorAll('[data-catalog-category]:not([data-catalog-all])')];
      if (input.matches('[data-catalog-all]') && input.checked) {
        categoryInputs.forEach((category) => { category.checked = false; });
      } else if (!input.matches('[data-catalog-all]')) {
        if (allCategories) allCategories.checked = false;
      }
      const selectedCategories = categoryInputs.filter((category) => category.checked).map((category) => category.value);
      if (!selectedCategories.length && allCategories) allCategories.checked = true;
      if (window.matchMedia('(max-width: 989px)').matches) return;
      this.loadCategorySelection(selectedCategories, allCategories?.dataset.catalogAllUrl || '/collections/all');
      return;
    }
    if ((input instanceof HTMLInputElement || input instanceof HTMLSelectElement) && input.closest('[data-catalog-filter-form], [data-catalog-sort-form]')) {
      if (input.closest('[data-catalog-filter-form]') && window.matchMedia('(max-width: 989px)').matches) return;
      if (input instanceof HTMLSelectElement && input.closest('[data-catalog-sort-form]')) this.syncCatalogSortWidth(input);
      this.applyCatalogFilters(input.form);
      return;
    }
    if (!(input instanceof HTMLInputElement) || !input.matches('[data-option-input]')) return;
    const form = input.closest('form');
    if (!form) return;
    const values = [...form.querySelectorAll('[data-option-input]:checked')].map((option) => option.value);
    const variants = JSON.parse(form.querySelector('[data-variants-json]')?.textContent || '[]');
    const variant = variants.find((item) => item.options.every((value, index) => value === values[index]));
    const idInput = form.querySelector('[name="id"]');
    const button = form.querySelector('[type="submit"]');
    if (variant && idInput) idInput.value = variant.id;
    if (variant) {
      const productInfo = form.closest('.product-info');
      this.updateProductPrice(productInfo?.querySelector('[data-product-price]'), variant);
      const meta = form.querySelector('[data-product-meta]');
      if (meta) {
        meta.textContent = variant.available ? (meta.dataset.stockLabel || 'In stock') : '';
      }
    }
    if (button) {
      button.disabled = !variant || !variant.available;
      button.querySelector('span').textContent = variant && variant.available ? button.dataset.addLabel : button.dataset.soldLabel;
    }
  }

  updateProductPrice(container, variant) {
    if (!(container instanceof HTMLElement)) return;
    const currency = container.dataset.currency || 'QAR';
    const fragment = document.createDocumentFragment();
    if (Number(variant.compare_at_price || 0) > Number(variant.price || 0)) {
      const comparison = document.createElement('s');
      comparison.textContent = this.formatCartMoney(variant.compare_at_price, currency);
      fragment.append(comparison);
    }
    const current = document.createElement('span');
    current.textContent = this.formatCartMoney(variant.price, currency);
    fragment.append(current);
    container.replaceChildren(fragment);
  }

  openSearch() {
    this.closeCart();
    this.closeAccountMenu();
    const searchBar = this.searchShell?.querySelector('.header-search__bar');
    const collapsedRect = searchBar?.getBoundingClientRect();
    if (this.searchCollapseTimer) clearTimeout(this.searchCollapseTimer);
    this.searchCollapseTimer = null;
    this.searchShell?.classList.add('is-expanded');
    this.searchShell?.classList.add('is-open');
    this.searchResultsPanel?.setAttribute('aria-hidden', 'false');
    this.searchShell?.querySelector('[data-search-open]')?.setAttribute('aria-expanded', 'true');
    document.querySelector('.site-header')?.classList.add('search-open');
    if (
      searchBar instanceof HTMLElement
      && collapsedRect
      && window.matchMedia('(max-width: 640px)').matches
      && !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      const expandedRect = searchBar.getBoundingClientRect();
      const scaleX = collapsedRect.width / Math.max(expandedRect.width, 1);
      if (this.searchExpansionTimer) clearTimeout(this.searchExpansionTimer);
      searchBar.style.transition = 'none';
      searchBar.style.transformOrigin = 'left center';
      searchBar.style.willChange = 'transform';
      searchBar.style.transform = `translate3d(${collapsedRect.left - expandedRect.left}px,${collapsedRect.top - expandedRect.top}px,0) scaleX(${scaleX})`;
      void searchBar.offsetWidth;
      requestAnimationFrame(() => {
        searchBar.style.transition = 'transform 240ms cubic-bezier(.16,1,.3,1)';
        searchBar.style.transform = 'translate3d(0,0,0) scaleX(1)';
      });
      this.searchExpansionTimer = setTimeout(() => {
        searchBar.style.removeProperty('transition');
        searchBar.style.removeProperty('transform-origin');
        searchBar.style.removeProperty('will-change');
        searchBar.style.removeProperty('transform');
        this.searchExpansionTimer = null;
      }, 280);
    }
    const input = this.searchShell?.querySelector('[data-predictive-input]');
    input?.focus({ preventScroll: true });
    if (input instanceof HTMLInputElement) input.setSelectionRange(input.value.length, input.value.length);
  }

  closeSearch() {
    if (this.searchExpansionTimer) clearTimeout(this.searchExpansionTimer);
    this.searchExpansionTimer = null;
    const searchBar = this.searchShell?.querySelector('.header-search__bar');
    if (searchBar instanceof HTMLElement) {
      searchBar.style.removeProperty('transition');
      searchBar.style.removeProperty('transform-origin');
      searchBar.style.removeProperty('will-change');
      searchBar.style.removeProperty('transform');
    }
    this.searchShell?.classList.remove('is-open');
    this.searchResultsPanel?.setAttribute('aria-hidden', 'true');
    this.searchShell?.querySelector('[data-search-open]')?.setAttribute('aria-expanded', 'false');
    if (this.searchCollapseTimer) clearTimeout(this.searchCollapseTimer);
    this.searchCollapseTimer = setTimeout(() => {
      this.searchShell?.classList.remove('is-expanded');
      document.querySelector('.site-header')?.classList.remove('search-open');
      this.searchCollapseTimer = null;
    }, 110);
  }

  collapseCatalogSearch(clear = false) {
    if (!window.matchMedia('(max-width: 989px)').matches) return;
    const shell = document.querySelector('[data-catalog-search-shell].is-expanded');
    if (!shell) return;
    const input = shell.querySelector('[data-catalog-search]');
    shell.classList.remove('is-expanded');
    shell.closest('.collection-toolbar__actions')?.classList.remove('is-search-expanded');
    shell.querySelector('[data-catalog-search-toggle]')?.setAttribute('aria-expanded', 'false');
    input?.blur();
    if (clear && input instanceof HTMLInputElement && input.value) {
      input.value = '';
      this.filterCatalog('');
    }
  }

  toggleProductDetails(details) {
    if (!(details instanceof HTMLDetailsElement) || details.dataset.animating === 'true') return;
    const content = details.querySelector('[data-product-details-content]');
    if (!(content instanceof HTMLElement)) return;
    const opening = !details.open;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      details.open = opening;
      return;
    }

    if (opening) details.open = true;
    details.dataset.animating = 'true';
    details.classList.toggle('is-collapsing', !opening);
    const contentHeight = content.scrollHeight;
    const animation = content.animate(
      [
        { height: `${opening ? 0 : contentHeight}px`, opacity: opening ? 0 : 1 },
        { height: `${opening ? contentHeight : 0}px`, opacity: opening ? 1 : 0 }
      ],
      { duration: 280, easing: 'cubic-bezier(.2,.8,.2,1)', fill: 'forwards' }
    );

    animation.addEventListener('finish', () => {
      if (!opening) details.open = false;
      animation.cancel();
      details.classList.remove('is-collapsing');
      delete details.dataset.animating;
    }, { once: true });
  }

  closeCatalogFilter() {
    const panel = document.querySelector('[data-catalog-filter-panel]');
    panel?.classList.remove('is-open');
    document.querySelectorAll('[data-catalog-filter-toggle]').forEach((toggle) => {
      toggle.setAttribute('aria-expanded', 'false');
    });
  }

  syncCatalogSortWidth(select = document.querySelector('[data-catalog-sort-form] select')) {
    if (!(select instanceof HTMLSelectElement)) return;
    if (!window.matchMedia('(max-width: 989px)').matches) {
      select.style.removeProperty('width');
      return;
    }
    const option = select.options[select.selectedIndex];
    if (!option) return;
    const probe = document.createElement('span');
    const styles = getComputedStyle(select);
    probe.textContent = option.textContent;
    probe.style.cssText = 'position:fixed;left:-9999px;top:-9999px;visibility:hidden;white-space:nowrap';
    probe.style.font = styles.font;
    probe.style.letterSpacing = styles.letterSpacing;
    document.body.append(probe);
    const contentWidth = Math.ceil(probe.getBoundingClientRect().width);
    probe.remove();
    const maximumWidth = Math.max(window.innerWidth - 206, 100);
    select.style.width = `${Math.min(contentWidth + 64, maximumWidth)}px`;
  }

  getCatalogSearchInput() {
    const selector = window.matchMedia('(max-width: 989px)').matches
      ? '[data-catalog-search-mobile]'
      : '[data-catalog-search-desktop]';
    return document.querySelector(selector);
  }

  openCart() {
    this.closeSearch();
    this.closeAccountMenu();
    if (this.cartBackdropTimer) clearTimeout(this.cartBackdropTimer);
    if (this.cartDrawerTimer) clearTimeout(this.cartDrawerTimer);
    this.cartBackdropTimer = null;
    this.cartDrawerTimer = null;
    if (this.cartBackdrop) {
      this.cartBackdrop.hidden = false;
    }
    if (this.cartDrawer) this.cartDrawer.hidden = false;
    requestAnimationFrame(() => {
      this.cartBackdrop?.classList.add('is-open');
      this.cartDrawer?.classList.add('is-open');
    });
    this.cartDrawer?.setAttribute('aria-hidden', 'false');
    document.body.classList.add('drawer-open');
    if (this.cartIsStale) {
      this.refreshCart({ animateNew: this.pendingCartItemAnimation }).catch(() => { this.cartIsStale = true; });
    }
  }

  closeCart() {
    this.cartDrawer?.classList.remove('is-open');
    this.cartBackdrop?.classList.remove('is-open');
    if (this.cartBackdropTimer) clearTimeout(this.cartBackdropTimer);
    this.cartBackdropTimer = setTimeout(() => {
      if (this.cartBackdrop && !this.cartBackdrop.classList.contains('is-open')) this.cartBackdrop.hidden = true;
      this.cartBackdropTimer = null;
    }, 220);
    if (this.cartDrawerTimer) clearTimeout(this.cartDrawerTimer);
    this.cartDrawerTimer = setTimeout(() => {
      if (this.cartDrawer && !this.cartDrawer.classList.contains('is-open')) this.cartDrawer.hidden = true;
      this.syncBrowserChromeColor();
      this.cartDrawerTimer = null;
    }, 320);
    this.cartDrawer?.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('drawer-open');
  }

  toggleAccountMenu() {
    if (this.accountPopover?.classList.contains('is-open')) {
      this.closeAccountMenu();
      return;
    }
    this.closeSearch();
    this.closeCart();
    if (!this.accountPopover) return;
    if (this.accountPopoverTimer) clearTimeout(this.accountPopoverTimer);
    this.accountPopoverTimer = null;
    this.accountPopover.hidden = false;
    requestAnimationFrame(() => this.accountPopover?.classList.add('is-open'));
    this.accountMenu?.classList.add('is-open');
    this.accountPopover.setAttribute('aria-hidden', 'false');
    this.accountMenu?.querySelector('[data-account-trigger]')?.setAttribute('aria-expanded', 'true');
  }

  closeAccountMenu() {
    if (!this.accountPopover) return;
    this.accountPopover.classList.remove('is-open');
    this.accountMenu?.classList.remove('is-open');
    this.accountPopover.setAttribute('aria-hidden', 'true');
    this.accountMenu?.querySelector('[data-account-trigger]')?.setAttribute('aria-expanded', 'false');
    if (this.accountPopoverTimer) clearTimeout(this.accountPopoverTimer);
    this.accountPopoverTimer = setTimeout(() => {
      if (this.accountPopover && !this.accountPopover.classList.contains('is-open')) this.accountPopover.hidden = true;
      this.accountPopoverTimer = null;
    }, 100);
  }

  closeOverlays() { this.closeSearch(); this.closeCart(); this.closeAccountMenu(); this.closeConfirmation(); }

  toggleTheme(button) {
    const dark = document.documentElement.classList.contains('theme-dark');
    const nextTheme = dark ? 'light' : 'dark';
    try { localStorage.setItem('moments-gifts-theme', nextTheme); } catch (error) {}
    document.documentElement.className = `theme-${nextTheme}`;
    if (button instanceof HTMLElement) button.blur();
    this.syncBrowserChromeColor();
  }

  changeQuantity(button) {
    const wrapper = button.closest('.quantity');
    const input = wrapper?.querySelector('input');
    if (!input) return;
    const next = Math.max(Number(input.min || 1), Number(input.value || 1) + Number(button.dataset.quantityChange));
    input.value = String(next);
  }

  changeCartLine(button) {
    const quantityControl = button.closest('[data-drawer-quantity]');
    const quantityLabel = quantityControl?.querySelector('span');
    const currentQuantity = Number(quantityLabel?.textContent || button.dataset.cartQuantity || 1);
    const nextQuantity = Math.max(1, currentQuantity + Number(button.dataset.cartLineChange || 0));
    if (!quantityControl || !quantityLabel || nextQuantity === currentQuantity) return;
    quantityLabel.textContent = String(nextQuantity);
    quantityControl.querySelector('[data-cart-line-change="-1"]')?.toggleAttribute('disabled', nextQuantity <= 1);
    const changed = [...this.cartDrawer.querySelectorAll('[data-drawer-quantity]')].some((control) => {
      const quantity = Number(control.querySelector('span')?.textContent || 1);
      return quantity !== Number(control.dataset.originalQuantity || 1);
    });
    this.setCartApplyVisible(changed);
  }

  setCartApplyVisible(visible) {
    const applyButton = this.cartDrawer?.querySelector('[data-cart-apply]');
    const applyShell = applyButton?.closest('[data-cart-apply-shell]');
    if (!applyButton || !applyShell) return;
    applyShell.classList.toggle('is-visible', visible);
    applyShell.setAttribute('aria-hidden', String(!visible));
    applyButton.tabIndex = visible ? 0 : -1;
  }

  setCartApplyLoading(button, loading) {
    if (!button) return;
    const label = button.querySelector('[data-cart-apply-label]');
    button.disabled = loading;
    button.classList.toggle('is-loading', loading);
    button.setAttribute('aria-busy', String(loading));
    if (label) label.textContent = loading ? window.momentsGifts.strings.applyingChanges : window.momentsGifts.strings.applyChanges;
  }

  async applyCartChanges(button) {
    const controls = [...this.cartDrawer.querySelectorAll('[data-drawer-quantity]')];
    const updates = {};
    controls.forEach((control) => {
      if (control.dataset.cartKey) updates[control.dataset.cartKey] = Number(control.querySelector('span')?.textContent || 1);
    });
    this.setCartApplyLoading(button, true);
    controls.forEach((control) => control.classList.add('is-loading'));
    try {
      const response = await fetch(window.momentsGifts.routes.cartUpdate + '.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });
      if (!response.ok) throw new Error(window.momentsGifts.strings.error);
      const cart = await response.json();
      this.syncCartDrawer(cart);
      if (document.querySelector('.cart-page')) this.refreshCartPage();
    } catch (error) {
      this.setCartApplyLoading(button, false);
      controls.forEach((control) => control.classList.remove('is-loading'));
    }
  }

  replaceCartDrawer(markup) {
    const page = new DOMParser().parseFromString(markup, 'text/html');
    const incoming = page.querySelector('[data-cart-drawer]');
    if (!incoming || !this.cartDrawer) return;
    this.cartDrawer.innerHTML = incoming.innerHTML;
    this.cartDrawer.dataset.cartItemCount = incoming.dataset.cartItemCount || '0';
    document.querySelectorAll('[data-cart-count]').forEach((count) => {
      count.textContent = this.cartDrawer.dataset.cartItemCount;
    });
    this.cartIsStale = false;
  }

  formatCartMoney(cents, currency) {
    const currencyCode = currency || this.cartDrawer?.dataset.cartCurrency || 'QAR';
    try {
      const amount = new Intl.NumberFormat('en-US', {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0
      }).format(Number(cents || 0) / 100);
      return `${amount} ${currencyCode}`;
    } catch (error) {
      return `${Math.round(Number(cents || 0) / 100)} ${currencyCode}`;
    }
  }

  animateEmptyState(emptyState) {
    if (!emptyState || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    emptyState.animate([
      { opacity: 0, transform: 'translate3d(0,12px,0) scale(.985)' },
      { opacity: 1, transform: 'translate3d(0,0,0) scale(1)' }
    ], {
      duration: 320,
      easing: 'cubic-bezier(.16,1,.3,1)',
      fill: 'both'
    });
  }

  showOptimisticEmptyCart() {
    if (this.cartDrawer) {
      this.cartDrawer.querySelectorAll(':scope > :not(.cart-drawer__head)').forEach((element) => element.remove());
      const drawerTemplate = document.querySelector('[data-cart-empty-template]');
      if (drawerTemplate instanceof HTMLTemplateElement) {
        this.cartDrawer.append(drawerTemplate.content.cloneNode(true));
        this.animateEmptyState(this.cartDrawer.querySelector(':scope > .empty-state'));
      }
      this.cartDrawer.dataset.cartItemCount = '0';
    }
    document.querySelectorAll('[data-cart-count]').forEach((count) => { count.textContent = '0'; });

    const page = document.querySelector('.cart-page');
    const pageTemplate = document.querySelector('[data-cart-page-empty-template]');
    if (page && pageTemplate instanceof HTMLTemplateElement) {
      page.classList.add('cart-page--empty');
      page.querySelector(':scope > .cart-summary')?.remove();
      const content = page.querySelector(':scope > div');
      content?.querySelector('form')?.remove();
      content?.append(pageTemplate.content.cloneNode(true));
      this.animateEmptyState(content?.querySelector(':scope > .empty-state'));
    }
    this.cartIsStale = false;
  }

  syncCartDrawer(cart) {
    if (!this.cartDrawer) return;
    this.cartDrawer.dataset.cartItemCount = String(cart.item_count || 0);
    if (cart.currency) this.cartDrawer.dataset.cartCurrency = cart.currency;
    document.querySelectorAll('[data-cart-count]').forEach((count) => { count.textContent = String(cart.item_count || 0); });

    const itemsByKey = new Map((cart.items || []).map((item) => [item.key, item]));
    this.cartDrawer.querySelectorAll('[data-cart-item]').forEach((row) => {
      const item = itemsByKey.get(row.dataset.cartKey);
      if (!item) {
        row.remove();
        return;
      }
      const control = row.querySelector('[data-drawer-quantity]');
      const quantity = control?.querySelector('span');
      if (control) {
        control.dataset.originalQuantity = String(item.quantity);
        control.classList.remove('is-loading');
      }
      if (quantity) quantity.textContent = String(item.quantity);
      control?.querySelector('[data-cart-line-change="-1"]')?.toggleAttribute('disabled', item.quantity <= 1);
      control?.querySelector('[data-cart-line-change="1"]')?.removeAttribute('disabled');
      const price = row.querySelector('[data-cart-line-price]');
      if (price) price.textContent = this.formatCartMoney(item.final_line_price, cart.currency);
    });

    this.cartDrawer.querySelectorAll('[data-cart-item]').forEach((row, index) => {
      row.querySelectorAll('[data-cart-line-change], [data-confirm-remove]').forEach((control) => {
        control.dataset.cartLine = String(index + 1);
      });
    });
    const total = this.cartDrawer.querySelector('[data-cart-total]');
    if (total) total.textContent = this.formatCartMoney(cart.total_price, cart.currency);
    const apply = this.cartDrawer.querySelector('[data-cart-apply]');
    if (apply) {
      this.setCartApplyLoading(apply, false);
      this.setCartApplyVisible(false);
    }
    if (!cart.item_count) {
      this.cartDrawer.querySelectorAll(':scope > :not(.cart-drawer__head)').forEach((element) => element.remove());
      const template = document.querySelector('[data-cart-empty-template]');
      if (template instanceof HTMLTemplateElement) {
        this.cartDrawer.append(template.content.cloneNode(true));
        this.animateEmptyState(this.cartDrawer.querySelector(':scope > .empty-state'));
      }
    }
    this.cartIsStale = false;
  }

  normalizeCatalogSearch(value) {
    return value
      .toLocaleLowerCase()
      .normalize('NFKD')
      .replace(/[\u064B-\u065F\u0670\u0640]/g, '')
      .replace(/[إأآٱ]/g, 'ا')
      .replace(/ى/g, 'ي')
      .replace(/ة/g, 'ه')
      .replace(/ؤ/g, 'و')
      .replace(/ئ/g, 'ي')
      .trim();
  }

  filterCatalog(query) {
    const normalizedQuery = this.normalizeCatalogSearch(query);
    const selectedCategories = [...document.querySelectorAll('[data-catalog-category]:not([data-catalog-all]):checked')].map((category) => category.value);
    const products = [...document.querySelectorAll('[data-catalog-product]')];
    let visibleCount = 0;
    products.forEach((product) => {
      const productCategories = product.dataset.catalogCategories?.trim().split(/\s+/) || [];
      const searchableText = this.normalizeCatalogSearch(`${product.dataset.catalogSearchText || ''} ${product.textContent || ''}`);
      const matchesSearch = !normalizedQuery || searchableText.includes(normalizedQuery);
      const matchesCategory = !selectedCategories.length || selectedCategories.some((category) => productCategories.includes(category));
      const visible = matchesSearch && matchesCategory;
      product.hidden = !visible;
      if (visible) visibleCount += 1;
    });
    const count = document.querySelector('[data-catalog-count]');
    if (count) {
      const label = visibleCount === 1 ? count.dataset.singular : count.dataset.plural;
      count.textContent = `${visibleCount} ${label}`;
    }
    const empty = document.querySelector('[data-catalog-empty]');
    if (empty) empty.hidden = visibleCount !== 0;
  }

  async loadCategorySelection(categories, allProductsUrl) {
    const parameters = new URLSearchParams(window.location.search);
    if (categories.length) parameters.set('categories', categories.join(','));
    else parameters.delete('categories');
    const destination = `${allProductsUrl}${parameters.size ? `?${parameters.toString()}` : ''}`;
    await this.navigateCatalog(destination);
  }

  restoreCategorySelection(url) {
    if (!document.querySelector('.collection-layout')) return;
    const parameters = new URL(url, window.location.origin).searchParams;
    if (!parameters.has('categories')) return;
    const categories = parameters.get('categories')?.split(',').filter(Boolean) || [];
    const allCategories = document.querySelector('[data-catalog-category][data-catalog-all]');
    const categoryInputs = [...document.querySelectorAll('[data-catalog-category]:not([data-catalog-all])')];
    categoryInputs.forEach((category) => { category.checked = categories.includes(category.value); });
    if (allCategories) allCategories.checked = categories.length === 0;
    const search = this.getCatalogSearchInput();
    this.filterCatalog(search instanceof HTMLInputElement ? search.value : '');
  }

  focusCatalogSearchFromUrl(url) {
    const parameters = new URL(url, window.location.origin).searchParams;
    if (parameters.get('focus') !== 'search') return;
    const search = this.getCatalogSearchInput();
    if (!(search instanceof HTMLInputElement)) return;
    search.value = parameters.get('q') || '';
    const shell = search.closest('[data-catalog-search-shell]');
    shell?.classList.add('is-expanded');
    shell?.closest('.collection-toolbar__actions')?.classList.add('is-search-expanded');
    shell?.querySelector('[data-catalog-search-toggle]')?.setAttribute('aria-expanded', 'true');
    this.filterCatalog(search.value);
    search.focus({ preventScroll: true });
    search.setSelectionRange(search.value.length, search.value.length);
  }

  applyCatalogFilters(form) {
    if (!(form instanceof HTMLFormElement)) return;
    const parameters = new URLSearchParams(window.location.search);
    let destinationPath = window.location.pathname;
    if (form.matches('[data-catalog-filter-form]')) {
      [...parameters.keys()].forEach((key) => { if (key.startsWith('filter.')) parameters.delete(key); });
      new FormData(form).forEach((value, key) => parameters.append(key, String(value)));
      const allCategories = form.querySelector('[data-catalog-category][data-catalog-all]');
      const selectedCategories = [...form.querySelectorAll('[data-catalog-category]:not([data-catalog-all]):checked')]
        .map((category) => category.value)
        .filter(Boolean);
      parameters.delete('categories');
      if (selectedCategories.length) parameters.set('categories', selectedCategories.join(','));
      destinationPath = allCategories?.dataset.catalogAllUrl || destinationPath;
    } else {
      new FormData(form).forEach((value, key) => parameters.set(key, String(value)));
    }
    const destination = parameters.size ? `${destinationPath}?${parameters.toString()}` : destinationPath;
    this.navigateCatalog(destination);
  }

  async navigateCatalog(url, updateHistory = true) {
    const currentHero = document.querySelector('.collection-hero');
    const currentLayout = document.querySelector('.collection-layout');
    if (!currentHero || !currentLayout) return;
    currentLayout.classList.add('is-loading');
    try {
      const response = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) throw new Error(window.momentsGifts.strings.error);
      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextHero = page.querySelector('.collection-hero');
      const nextLayout = page.querySelector('.collection-layout');
      if (!nextHero || !nextLayout) throw new Error(window.momentsGifts.strings.error);
      nextHero.classList.add('catalog-enter');
      nextLayout.classList.add('catalog-enter');
      currentHero.replaceWith(nextHero);
      currentLayout.replaceWith(nextLayout);
      document.title = page.title;
      if (updateHistory) window.history.pushState({}, '', url);
      this.restoreCategorySelection(url);
      this.focusCatalogSearchFromUrl(url);
      this.syncCatalogSortWidth();
    } catch (error) {
      currentLayout.classList.remove('is-loading');
    }
  }

  async predict(query) {
    const results = this.searchShell?.querySelector('[data-predictive-results]');
    const advanced = this.searchShell?.querySelector('[data-advanced-search]');
    if (!results) return;
    const normalizedQuery = query.trim();
    if (advanced) {
      const parameters = new URLSearchParams({ focus: 'search' });
      if (normalizedQuery) parameters.set('q', normalizedQuery);
      advanced.href = `${window.momentsGifts.routes.allProducts || '/collections/all'}?${parameters.toString()}`;
    }
    if (normalizedQuery.length < 1) {
      results.innerHTML = `<p class="muted" data-search-idle>${window.momentsGifts.strings.searchIdle}</p>`;
      return;
    }
    try {
      const url = `${window.momentsGifts.routes.predictiveSearch}?q=${encodeURIComponent(normalizedQuery)}&resources[type]=product&resources[limit]=5&section_id=predictive-search`;
      const response = await fetch(url);
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      const predictiveContent = html.querySelector('[data-predictive-content]');
      const predictiveMatches = predictiveContent?.querySelectorAll('.predictive-item').length || 0;
      if (predictiveMatches) {
        results.innerHTML = predictiveContent.innerHTML;
        return;
      }
      this.renderLocalizedSearchFallback(normalizedQuery, results);
    } catch (error) { this.renderLocalizedSearchFallback(normalizedQuery, results); }
  }

  renderLocalizedSearchFallback(query, results) {
    const index = document.querySelector('template[data-localized-search-index]');
    if (!(index instanceof HTMLTemplateElement)) return;
    const matches = [...index.content.querySelectorAll('[data-localized-search-item]')]
      .filter((item) => this.normalizeCatalogSearch(item.getAttribute('data-search-text') || '').includes(this.normalizeCatalogSearch(query)))
      .slice(0, 5);
    if (!matches.length) {
      results.innerHTML = `<p class="muted">${window.momentsGifts.strings.searchNoResults || window.momentsGifts.strings.searchIdle}</p>`;
      return;
    }
    const fragment = document.createDocumentFragment();
    matches.forEach((item) => fragment.append(item.cloneNode(true)));
    results.replaceChildren(fragment);
  }

  async addToCart(form) {
    const button = form.querySelector('[type="submit"]');
    const quickAddButton = button?.matches('.quick-add__button') ? button : null;
    const label = button?.querySelector('span');
    const originalLabel = label?.textContent;
    const status = form.querySelector('[data-product-status]');
    const formData = new FormData(form);
    const runtime = this.runtime?.routes ? this.runtime : this.readRuntime();
    const action = form.getAttribute('action') || runtime.routes.cartAdd || '/cart/add';
    const endpoint = action.endsWith('.js') ? action : `${action}.js`;
    const addedQuantity = Math.max(1, Number(formData.get('quantity') || 1));
    if (button) button.disabled = true;
    if (quickAddButton) {
      quickAddButton.classList.remove('is-added');
      quickAddButton.classList.add('is-loading');
      quickAddButton.setAttribute('aria-busy', 'true');
    }
    try {
      const response = await fetch(endpoint, { method: 'POST', headers: { Accept: 'application/json' }, body: formData });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.description || runtime.strings.error || 'Something went wrong. Please try again.');
      }
      await response.json();
      document.querySelectorAll('[data-cart-count]').forEach((count) => {
        count.textContent = String(Number(count.textContent || 0) + addedQuantity);
      });
      const cartIcon = document.querySelector('[data-cart-open] .interface-icon');
      if (cartIcon) {
        cartIcon.classList.remove('cart-bag-pulse');
        void cartIcon.getBoundingClientRect();
        cartIcon.classList.add('cart-bag-pulse');
        setTimeout(() => cartIcon.classList.remove('cart-bag-pulse'), 600);
      }
      this.cartIsStale = true;
      this.pendingCartItemAnimation = true;
      if (this.cartDrawer?.classList.contains('is-open')) {
        this.refreshCart({ animateNew: true }).catch(() => { this.cartIsStale = true; });
      }
      if (label) label.textContent = runtime.strings.added || 'Added to cart';
      quickAddButton?.classList.remove('is-loading');
      button?.classList.add('is-added');
      if (status) status.textContent = '';
      setTimeout(() => {
        if (label && originalLabel) label.textContent = originalLabel;
        button?.classList.remove('is-added');
        if (status) status.textContent = '';
      }, 900);
    } catch (error) {
      if (status) status.textContent = error.message;
    } finally {
      if (quickAddButton) {
        quickAddButton.classList.remove('is-loading');
        quickAddButton.setAttribute('aria-busy', 'false');
      }
      if (button) button.disabled = false;
    }
  }

  async refreshCart({ animateNew = false } = {}) {
    const refreshId = ++this.cartRefreshId;
    const existingKeys = new Set([...(this.cartDrawer?.querySelectorAll('[data-cart-item]') || [])].map((item) => item.dataset.cartKey));
    const response = await fetch(window.momentsGifts.routes.cart, { cache: 'no-store' });
    if (!response.ok) throw new Error(window.momentsGifts.strings.error);
    const text = await response.text();
    const html = new DOMParser().parseFromString(text, 'text/html');
    const incoming = html.querySelector('[data-cart-drawer]');
    if (refreshId !== this.cartRefreshId) return;
    if (incoming && this.cartDrawer) {
      this.cartDrawer.innerHTML = incoming.innerHTML;
      this.cartDrawer.dataset.cartItemCount = incoming.dataset.cartItemCount || '0';
      document.querySelectorAll('[data-cart-count]').forEach((count) => {
        count.textContent = this.cartDrawer.dataset.cartItemCount;
      });
      if (animateNew && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        [...this.cartDrawer.querySelectorAll('[data-cart-item]')]
          .filter((item) => !existingKeys.has(item.dataset.cartKey))
          .forEach((item, index) => {
            item.animate([
              { opacity: 0, transform: 'translate3d(14px,8px,0) scale(.985)' },
              { opacity: 1, transform: 'translate3d(0,0,0) scale(1)' }
            ], {
              duration: 320,
              delay: 70 + (index * 45),
              easing: 'cubic-bezier(.16,1,.3,1)',
              fill: 'both'
            });
          });
      }
      this.cartIsStale = false;
      if (animateNew) this.pendingCartItemAnimation = false;
    }
  }

  openConfirmation(button) {
    const dialog = document.querySelector('[data-confirm-dialog]');
    const backdrop = document.querySelector('[data-dialog-backdrop]');
    if (!dialog || !backdrop) return;
    const confirmButton = dialog.querySelector('[data-dialog-confirm]');
    confirmButton.dataset.cartLine = button.dataset.cartLine;
    confirmButton.dataset.cartSurface = button.closest('[data-cart-drawer]') ? 'drawer' : 'page';
    this.cartDrawer?.classList.toggle('is-confirming', confirmButton.dataset.cartSurface === 'drawer');
    confirmButton.disabled = false;
    backdrop.hidden = false;
    dialog.hidden = false;
    requestAnimationFrame(() => dialog.classList.add('is-open'));
  }

  closeConfirmation() {
    const dialog = document.querySelector('[data-confirm-dialog]');
    const backdrop = document.querySelector('[data-dialog-backdrop]');
    if (!dialog || !backdrop) return;
    dialog.classList.remove('is-open');
    this.cartDrawer?.classList.remove('is-confirming');
    setTimeout(() => { dialog.hidden = true; backdrop.hidden = true; }, 200);
  }

  async removeCartItem(button) {
    const line = Number(button.dataset.cartLine);
    if (!line) return;
    button.disabled = true;
    const surface = button.dataset.cartSurface || (document.querySelector('.cart-page') ? 'page' : 'drawer');
    const hasCartPage = Boolean(document.querySelector('.cart-page'));
    const sourceItems = surface === 'drawer'
      ? this.cartDrawer?.querySelectorAll('[data-cart-item]')
      : document.querySelectorAll('.cart-page [data-cart-page-item]');
    const removesLastLine = sourceItems?.length === 1;
    const removalAnimation = surface === 'drawer'
      ? this.animateCartDrawerRemoval(line)
      : this.animateCartPageRemoval(line);
    this.closeConfirmation();
    const requestResult = fetch(window.momentsGifts.routes.cartChange + '.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ line, quantity: 0 })
    }).then(async (response) => {
      if (!response.ok) throw new Error(window.momentsGifts.strings.error);
      return { cart: await response.json() };
    }).catch((error) => ({ error }));

    if (removesLastLine) {
      await removalAnimation;
      this.showOptimisticEmptyCart();
    }
    try {
      const result = await requestResult;
      if (result.error) throw result.error;
      const cart = result.cart;
      if (!removesLastLine) await removalAnimation;
      if (removesLastLine && !cart.item_count) {
        this.cartIsStale = false;
        return;
      }
      this.syncCartDrawer(cart);
      if (hasCartPage) {
        if (removesLastLine && cart.item_count) await this.refreshCartPage();
        else if (cart.item_count) this.syncCartPage(cart);
        else await this.refreshCartPage(true);
      }
    } catch (error) {
      await removalAnimation;
      if (surface === 'drawer' || removesLastLine) await this.refreshCart();
      if (hasCartPage) await this.refreshCartPage();
    }
  }

  animateCartDrawerRemoval(line) {
    const removeButton = this.cartDrawer?.querySelector(`[data-confirm-remove][data-cart-line="${line}"]`);
    const row = removeButton?.closest('.drawer-item');
    if (!row) return Promise.resolve();
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      row.remove();
      return Promise.resolve();
    }

    const list = row.closest('.cart-drawer__items');
    const rows = list ? [...list.querySelectorAll('.drawer-item')] : [];
    const rowIndex = rows.indexOf(row);
    const followingRows = rowIndex >= 0 ? rows.slice(rowIndex + 1) : [];
    const firstPositions = new Map(followingRows.map((item) => [item, item.getBoundingClientRect().top]));
    const rowRect = row.getBoundingClientRect();
    const ghost = row.cloneNode(true);

    Object.assign(ghost.style, {
      position: 'fixed',
      left: `${rowRect.left}px`,
      top: `${rowRect.top}px`,
      width: `${rowRect.width}px`,
      height: `${rowRect.height}px`,
      margin: '0',
      zIndex: '108',
      pointerEvents: 'none',
      transformOrigin: 'center'
    });
    ghost.setAttribute('aria-hidden', 'true');
    document.body.append(ghost);
    row.remove();

    const movementAnimations = followingRows.map((item) => {
      const distance = firstPositions.get(item) - item.getBoundingClientRect().top;
      return item.animate([
        { transform: `translate3d(0,${distance}px,0)` },
        { transform: 'translate3d(0,0,0)' }
      ], {
        duration: 280,
        easing: 'cubic-bezier(.16,1,.3,1)',
        fill: 'both'
      }).finished;
    });
    const ghostAnimation = ghost.animate([
      { opacity: 1, transform: 'translate3d(0,0,0) scale(1)' },
      { opacity: 0, transform: 'translate3d(-14px,0,0) scale(.985)' }
    ], {
      duration: 180,
      easing: 'ease-out',
      fill: 'forwards'
    }).finished;

    return Promise.all([ghostAnimation, ...movementAnimations]).then(() => ghost.remove());
  }

  animateCartPageRemoval(line) {
    const removeButton = document.querySelector(`.cart-page [data-confirm-remove][data-cart-line="${line}"]`);
    const row = removeButton?.closest('.cart-item');
    if (!row) return Promise.resolve();
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      row.remove();
      return Promise.resolve();
    }

    const list = row.closest('.cart-items');
    const rows = list ? [...list.querySelectorAll('.cart-item')] : [];
    const rowIndex = rows.indexOf(row);
    const followingRows = rowIndex >= 0 ? rows.slice(rowIndex + 1) : [];
    const firstPositions = new Map(followingRows.map((item) => [item, item.getBoundingClientRect().top]));
    const rowRect = row.getBoundingClientRect();
    const ghost = row.cloneNode(true);

    Object.assign(ghost.style, {
      position: 'fixed',
      left: `${rowRect.left}px`,
      top: `${rowRect.top}px`,
      width: `${rowRect.width}px`,
      height: `${rowRect.height}px`,
      margin: '0',
      zIndex: '90',
      pointerEvents: 'none',
      transformOrigin: 'center'
    });
    ghost.setAttribute('aria-hidden', 'true');
    document.body.append(ghost);
    row.remove();

    const movementAnimations = followingRows.map((item) => {
      const distance = firstPositions.get(item) - item.getBoundingClientRect().top;
      return item.animate([
        { transform: `translate3d(0,${distance}px,0)` },
        { transform: 'translate3d(0,0,0)' }
      ], {
        duration: 280,
        easing: 'cubic-bezier(.16,1,.3,1)',
        fill: 'both'
      }).finished;
    });
    const ghostAnimation = ghost.animate([
      { opacity: 1, transform: 'translate3d(0,0,0) scale(1)' },
      { opacity: 0, transform: 'translate3d(-14px,0,0) scale(.985)' }
    ], {
      duration: 180,
      easing: 'ease-out',
      fill: 'forwards'
    }).finished;

    return Promise.all([ghostAnimation, ...movementAnimations]).then(() => ghost.remove());
  }

  syncCartPage(cart) {
    const page = document.querySelector('.cart-page');
    if (!page) return;
    const itemsByKey = new Map((cart.items || []).map((item) => [item.key, item]));
    const rows = [...page.querySelectorAll('[data-cart-page-item]')];

    rows.forEach((row, index) => {
      const item = itemsByKey.get(row.dataset.cartKey);
      if (!item) {
        row.remove();
        return;
      }
      const input = row.querySelector('input[name="updates[]"]');
      const price = row.querySelector('[data-cart-page-line-price]');
      const remove = row.querySelector('[data-confirm-remove]');
      if (input) input.value = String(item.quantity);
      if (price) price.textContent = this.formatCartMoney(item.final_line_price, cart.currency);
      if (remove) remove.dataset.cartLine = String(index + 1);
    });

    const subtotal = page.querySelector('[data-cart-page-subtotal]');
    const total = page.querySelector('[data-cart-page-total]');
    if (subtotal) subtotal.textContent = this.formatCartMoney(cart.items_subtotal_price, cart.currency);
    if (total) total.textContent = this.formatCartMoney(cart.total_price, cart.currency);
  }

  async refreshCartPage(animateEmpty = false) {
    const current = document.querySelector('.cart-page');
    if (!current) return;
    const response = await fetch(window.momentsGifts.routes.cart, { cache: 'no-store' });
    if (!response.ok) return;
    const page = new DOMParser().parseFromString(await response.text(), 'text/html');
    const incoming = page.querySelector('.cart-page');
    if (incoming) {
      current.replaceWith(incoming);
      if (animateEmpty) this.animateEmptyState(incoming.querySelector('.empty-state'));
    }
  }

  async changeLanguage(form) {
    if (this.languageChanging) return;
    const button = form.querySelector('[data-language-toggle]');
    const languageInput = form.querySelector('[data-language-code]');
    const nextLanguage = languageInput?.value;
    const targetUrl = form.querySelector('[data-language-url]')?.value;
    if (!(button instanceof HTMLButtonElement) || !['en', 'ar'].includes(nextLanguage) || !targetUrl) return;
    const localizedPageUrl = this.getLocalizedPageUrl(form, targetUrl);
    this.languageChanging = true;
    button.disabled = true;
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    this.closeOverlays();

    try {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const direction = nextLanguage === 'ar' ? 'ar' : 'en';
      if (!reduceMotion) {
        document.body.classList.add(`language-motion-leave-to-${direction}`);
        await new Promise((resolve) => setTimeout(resolve, 120));
      }
      const response = await fetch(localizedPageUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
        cache: 'no-store'
      });
      if (!response.ok) throw new Error(this.runtime.strings.error);
      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      const runtimeNode = page.getElementById('momentsGiftsRuntime');
      if (!page.body || !runtimeNode) throw new Error(this.runtime.strings.error);
      document.title = page.title;
      document.documentElement.lang = page.documentElement.lang;
      document.documentElement.dir = page.documentElement.dir;
      document.body.innerHTML = page.body.innerHTML;
      window.momentsGifts = JSON.parse(runtimeNode.textContent || '{}');
      this.runtime = window.momentsGifts;
      this.refreshDomReferences();
      this.setupCategoryRails();
      this.setupFeaturedCarousels();
      this.restoreCategorySelection(response.url);
      this.focusCatalogSearchFromUrl(response.url);
      if (response.url) history.replaceState({}, '', response.url);
      this.syncBrowserChromeColor();
      document.body.classList.remove('language-motion-leave-to-ar', 'language-motion-leave-to-en');
      if (!reduceMotion) {
        document.body.classList.add(`language-motion-enter-${direction}`);
        requestAnimationFrame(() => requestAnimationFrame(() => {
          setTimeout(() => document.body.classList.remove(`language-motion-enter-${direction}`), 380);
        }));
      }
    } catch (error) {
      document.body.classList.remove('language-motion-leave-to-ar', 'language-motion-leave-to-en');
      window.location.assign(localizedPageUrl);
    } finally {
      button.classList.remove('is-loading');
      button.disabled = false;
      button.setAttribute('aria-busy', 'false');
      this.languageChanging = false;
    }
  }

  getLocalizedPageUrl(form, fallbackUrl) {
    const currentRoot = form.querySelector('[data-language-current-root]')?.value;
    const targetRoot = form.querySelector('[data-language-target-root]')?.value;
    if (!currentRoot || !targetRoot) return new URL(fallbackUrl, window.location.origin);

    const currentUrl = new URL(window.location.href);
    const normalizeRoot = (root) => {
      const withLeadingSlash = root.startsWith('/') ? root : `/${root}`;
      return withLeadingSlash.endsWith('/') ? withLeadingSlash : `${withLeadingSlash}/`;
    };
    const activeRoot = normalizeRoot(currentRoot);
    const nextRoot = normalizeRoot(targetRoot);
    let relativePath = currentUrl.pathname;

    if (activeRoot !== '/' && (relativePath === activeRoot.slice(0, -1) || relativePath.startsWith(activeRoot))) {
      relativePath = relativePath.slice(activeRoot.length);
    } else {
      relativePath = relativePath.replace(/^\/+/, '');
    }

    const localizedUrl = new URL(`${nextRoot}${relativePath}`, window.location.origin);
    localizedUrl.search = currentUrl.search;
    localizedUrl.hash = currentUrl.hash;
    return localizedUrl;
  }
}

const initializeMomentsGiftsTheme = () => {
  if (!window.momentsGiftsTheme) window.momentsGiftsTheme = new MomentsGiftsTheme();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeMomentsGiftsTheme, { once: true });
} else {
  initializeMomentsGiftsTheme();
}

document.addEventListener('shopify:section:load', () => {
  initializeMomentsGiftsTheme();
  window.momentsGiftsTheme?.setupCategoryRails();
  window.momentsGiftsTheme?.setupFeaturedCarousels();
});
